import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { ButtonBase } from '@mui/material';

import CreateEditFolderModal from '~/components/folders/CreateEditFolderModal';
import Select from '~/components/ui/Select';
import TextInput from '~/components/ui/TextInput';
import { QR_CODE_FOLDER_FORM_NAME, QR_CODE_FORM_NAME } from '~/constants/qr-code';
import useQrCodeFolders from '~/hooks/useQrCodeFolders';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { COLORS } from '~/styles/colors';

type NameAndOrganizationBlockProps = {
  isStatic?: boolean;
};

export default function NameAndOrganizationBlock({ isStatic = false }: NameAndOrganizationBlockProps) {
  const { t } = useTranslation();

  const [isOpenCreateFolderModal, setIsOpenCreateFolderModal] = useState(false);

  const { control, setValue } = useFormContext();

  const { data: folders = [] } = useQrCodeFolders();

  const styles = useStyles({ isBorder: !!folders.length });

  const qrCodeNameFieldName = isStatic ? 'name' : QR_CODE_FORM_NAME;

  return (
    <CreateQRContentBlockLayout type="nameAndOrganization" defaultExpanded={false}>
      <Controller
        control={control}
        name={qrCodeNameFieldName}
        render={({ field: { value, onChange, onBlur } }) => {
          return (
            <TextInput
              id={qrCodeNameFieldName}
              value={value}
              onChange={onChange}
              onBlur={onBlur}
              helperText={value?.length >= 100 ? t('other:maxLength', { maxLength: 100 }) : ''}
              fullWidth
              inputProps={{ maxLength: 100 }}
              label={t('basicTemplateLabels:nameAndOrganisation.name')}
              placeholder={t('basicTemplateLabels:nameAndOrganisation.namePlaceholder')}
            />
          );
        }}
      />
      <Controller
        control={control}
        name={QR_CODE_FOLDER_FORM_NAME}
        render={({ field: { value, onChange, onBlur } }) => {
          return (
            <Select
              id={QR_CODE_FOLDER_FORM_NAME}
              items={folders.map((folder) => ({ label: folder.name, value: folder.id }))}
              onChange={onChange}
              onBlur={onBlur}
              displayEmpty
              fullWidth
              label={t('builder:nameAndFolder.folder')}
              value={value ?? ''}
              labelSx={{ marginTop: '16px' }}
              placeholder={t('builder:nameAndFolder.noFolder')}
              AdditionalTopMenuItem={
                <ButtonBase sx={styles.createFolder} onClick={() => setIsOpenCreateFolderModal(true)}>
                  + {t('builder:nameAndFolder.createFolder')}
                </ButtonBase>
              }
            />
          );
        }}
      />
      {isOpenCreateFolderModal && (
        <CreateEditFolderModal
          isOpen={isOpenCreateFolderModal}
          onClose={() => setIsOpenCreateFolderModal(false)}
          onSuccessCreate={(folderId) => setValue(QR_CODE_FOLDER_FORM_NAME, folderId)}
          type="create"
        />
      )}
    </CreateQRContentBlockLayout>
  );
}

function useStyles({ isBorder }) {
  return {
    createFolder: css`
      font-weight: 500;
      color: ${COLORS.primary};
      font-size: 16px;
      width: 100%;
      padding: 10px 0;
      justify-content: flex-start;
      border-bottom: ${isBorder ? 1 : 0}px solid ${COLORS.grey200};
    `,
  };
}
